import { ReactNode } from "react";
import { useNavigate } from "react-router";
import { FaBoxOpen, FaEnvelope, FaShoppingBag, FaUserPlus } from "react-icons/fa";
import { AdminNotification } from "../../Models/AdminNotification";
import { AdminNotificationCategory } from "../../Enums/AdminNotificationCategory";
import { useAdminNotifications } from "../../Contexts/AdminNotificationContext";
import { formatDateTimeToBR } from "../../Utils/DataFormatters";

export const ADMIN_NOTIFICATION_CATEGORY_LABELS: Record<AdminNotificationCategory, string> = {
  [AdminNotificationCategory.Order]: "Pedido",
  [AdminNotificationCategory.Customer]: "Cliente",
  [AdminNotificationCategory.Support]: "Atendimento",
  [AdminNotificationCategory.Product]: "Produto"
};

const CATEGORY_ICONS: Record<AdminNotificationCategory, ReactNode> = {
  [AdminNotificationCategory.Order]: <FaShoppingBag />,
  [AdminNotificationCategory.Customer]: <FaUserPlus />,
  [AdminNotificationCategory.Support]: <FaEnvelope />,
  [AdminNotificationCategory.Product]: <FaBoxOpen />
};

const formatRelativeTime = (createdAt: string): string => {
  const minutes = Math.floor((Date.now() - new Date(createdAt).getTime()) / 60000);

  if (minutes < 1) {
    return "agora";
  }
  if (minutes < 60) {
    return `há ${minutes} min`;
  }
  if (minutes < 60 * 24) {
    return `há ${Math.floor(minutes / 60)} h`;
  }
  return formatDateTimeToBR(createdAt);
};

type AdminNotificationItemProps = {
  notification: AdminNotification;
  // Compact rows truncate the message (bell dropdown); full rows show all of it
  compact?: boolean;
  onOpen?: () => void;
};

const AdminNotificationItem = ({ notification, compact = false, onOpen }: AdminNotificationItemProps) => {
  const navigate = useNavigate();
  const { markAsRead } = useAdminNotifications();

  const handleClick = () => {
    if (!notification.isRead) {
      void markAsRead(notification.id);
    }

    onOpen?.();

    if (!notification.link) {
      return;
    }

    if (notification.link.startsWith("mailto:")) {
      window.location.href = notification.link;
    } else {
      navigate(notification.link);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`w-full text-left flex gap-3 px-4 py-3 transition-colors hover:bg-gray-50 ${
        notification.isRead ? "" : "bg-primary-50"
      }`}
    >
      <span
        className={`mt-0.5 shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm ${
          notification.isRead ? "bg-gray-100 text-gray-500" : "bg-primary-600 text-white"
        }`}
      >
        {CATEGORY_ICONS[notification.category]}
      </span>

      <span className="flex-1 min-w-0">
        <span className="flex items-baseline justify-between gap-2">
          <span className={`text-sm truncate ${notification.isRead ? "text-gray-700" : "font-semibold text-gray-900"}`}>
            {notification.title}
          </span>
          <span className="shrink-0 text-xs text-gray-500">{formatRelativeTime(notification.createdAt)}</span>
        </span>
        {notification.message && (
          <span
            className={`block text-xs text-gray-600 mt-0.5 ${
              compact ? "truncate" : "whitespace-pre-line break-words"
            }`}
          >
            {notification.message}
          </span>
        )}
        <span className="block text-[11px] uppercase tracking-wide text-gray-400 mt-1">
          {ADMIN_NOTIFICATION_CATEGORY_LABELS[notification.category]}
        </span>
      </span>
    </button>
  );
};

export default AdminNotificationItem;
